/**
 * What THIS person may do, as the platform says it — `GET /me/capabilities` (bata-platform ADR
 * 1001, contract W0-8), DEC-01, bata-ui ADR 9022.
 *
 * DEC-01: every flow is designed by role capability, never by identity, and a control that would
 * 403 is not shown. The platform answers, for every declared (kind, act), whether the caller may
 * take it, where (the grant's bound filled with the caller's values), which roles grant it, and —
 * when not — why (`not_granted`, `unscoped`, `not_wired`, `no_route`). The answer is derived from
 * the route's own wired grant, so it and the route agree; this module never holds a second copy of
 * the policy (no role names here).
 *
 * **Read once per session.** The answer changes only when a role or a scope is assigned in
 * Keycloak, which a new session picks up; it is cached per signed-in subject for the session
 * (`staleTime` and `gcTime` infinite) and shared by every control on every screen.
 *
 * **Unknown is not "no".** While the read is loading, or when it fails or the platform predates it
 * (404), the verdict is `unknown` and a control renders as it did before this read existed: the
 * platform is still the judge on the write (ADR 2003's remembered 403). Hiding every decision
 * control because one read failed would turn a transient fault into "you may do nothing".
 *
 * **A missing (kind, act) is not allowed**, and an unknown kind is ignored (contract: "the UI must
 * ignore unknown kinds and treat a missing (kind, action) as not allowed").
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiMalformedProblemError, ApiProblemError, callOperation } from '@/api';
import { myCapabilities, type Capability, type MeCapabilities } from '@/api/generated';

import { useAccessGrants } from './grants';

/** The declared kinds a screen asks about — the contract's catalogue v1 names, verbatim. */
export type CapabilityKind =
  | 'parameter_change' | 'objective_config' | 'replenishment_proposal' | 'transfer' | 'price_action'
  | 'exit_cadence_proposal' | 'exit_lane' | 'alert' | 'exception_rule' | 'execution_pack' | 'simulation'
  | 'plan_version' | 'scenario'
  /** bata-platform ADR 7037: an exit consolidation simulation — `read` its status, `propose` one. */
  | 'exit_simulation'
  /** bata-platform ADR 6105: one write-back command — its units confirmation and its cancellation. */
  | 'write_back_command'
  /**
   * bata-platform#482: the activity register, `GET /foundations/activity` — who did what and when
   * (bata-ui ADR 9037). Read only; granted to the administrator and the auditor by the platform.
   */
  | 'activity'
  /**
   * bata-platform ADR 9029: «Attività utenti», `GET /admin/user-activity` and its per-person reads —
   * sign-ins, sessions and acts (bata-ui ADR 9059). Read only; the administrator's alone.
   */
  | 'user_activity'
  /**
   * bata-reqs ADR 9002 stage 3 (platform ADR 9049): a line of the one run's merged list — `dispose` is the
   * one approval process for every store-to-store move.
   */
  | 'move_line'
  /** Platform ADR 9049/9056: a simulation of the one run — `read` the market's, `propose` one. */
  | 'move_run_simulation'
  /** Platform ADR 9056: overlay an owner-governed key (the valuation, the realisation rates) in a simulation of the one run. */
  | 'move_run_simulation_valuation_overlay'
  /**
   * bata-platform#939: «Asignación inicial» — `read` the market's runs and the run's lists, `propose` a run or a simulation of
   * the first push (`POST /launch/initial/runs`).
   */
  | 'initial_allocation_run'
  /** bata-platform#939: a line of the first push — `dispose` approves, rejects or defers it (`POST /launch/dispositions`). */
  | 'initial_allocation_line'
  /** The launch chapter's own proposals, decided on the same write as the app's lines (`POST /launch/dispositions`). */
  | 'launch_proposal'
  /** Platform «Surtido»: an assortment plan — `read` the market's, `propose` one (ask for it). */
  | 'assortment_plan'
  /** Platform «Surtido»: a row of a plan — `dispose` is accepting or rejecting its target. */
  | 'assortment_plan_row';

export type CapabilityAction =
  | 'read' | 'propose' | 'approve' | 'dispose' | 'execute' | 'confirm' | 'configure' | 'reject' | 'withdraw'
  /** bata-platform ADR 2102 (bata-ui#166 W3-3): own an alert without deciding it; snooze it. */
  | 'assign'
  /** bata-platform ADR 2102: withdraw an alert's decision, appended. */
  | 'reopen';

/** Why an act is not open to the caller: the platform's reasons, plus two this build states. */
export type DeniedReason =
  | 'not_granted' | 'unscoped' | 'not_wired' | 'no_route'
  /** Allowed, but the object's market is outside the caller's countries for this act. */
  | 'out_of_scope'
  /** The platform did not list this (kind, act) at all. */
  | 'not_declared'
  /** A reason word this build does not know — carried verbatim in `word`. */
  | 'other';

export type CapabilityVerdict =
  | { readonly state: 'allowed'; readonly capability: Capability }
  | { readonly state: 'unknown' }
  | {
    readonly state: 'denied';
    readonly reason: DeniedReason;
    /** The platform's own word when `reason` is `other`. */
    readonly word: string | null;
    readonly grantedToRoles: readonly string[];
    /** For `out_of_scope`: the countries the caller may act in, and the one asked about. */
    readonly countries: readonly string[];
    readonly country: string | null;
  };

const KNOWN_REASONS: ReadonlySet<string> = new Set(['not_granted', 'unscoped', 'not_wired', 'no_route']);

/** Where the act would land: the object's market, when the screen knows it. */
export interface CapabilityWhere {
  readonly country?: string | null;
}

/**
 * The verdict for one (kind, act) over the platform's answer. Pure, so the tests assert it.
 *
 * Scope: only the market is checked, and only when the screen names it — the one dimension every
 * decision object here carries. Channels and hierarchy nodes are not checked here: the platform
 * rewrites the write's scope, and a line outside them is reported back as out of scope by the
 * write itself (`outOfScope…Ids`), never silently written.
 */
export function judgeCapability(
  answer: MeCapabilities | undefined,
  kind: CapabilityKind,
  action: CapabilityAction,
  where: CapabilityWhere = {},
): CapabilityVerdict {
  // A body without the catalogue (a malformed or foreign answer) says nothing about this person.
  if (answer === undefined || !Array.isArray(answer.capabilities)) return { state: 'unknown' };
  const capability = answer.capabilities.find((entry) => entry.kind === kind && entry.action === action);
  const denied = (reason: DeniedReason, extra: Partial<Extract<CapabilityVerdict, { state: 'denied' }>> = {}): CapabilityVerdict => ({
    state: 'denied', reason, word: null, grantedToRoles: capability?.grantedToRoles ?? [], countries: [], country: null, ...extra,
  });
  if (capability === undefined) return denied('not_declared');
  if (!capability.allowed) {
    const word = capability.reason ?? null;
    return word !== null && KNOWN_REASONS.has(word)
      ? denied(word as DeniedReason)
      : denied('other', { word: word ?? 'unstated' });
  }
  const country = where.country ?? null;
  const scope = capability.scope ?? null;
  if (country !== null && scope !== null && !scope.unrestricted) {
    const countries = scope.countries ?? [];
    if (!countries.includes(country)) return denied('out_of_scope', { countries, country });
  }
  return { state: 'allowed', capability };
}

/** Whether a control renders: allowed, or not known yet (the platform judges the write). */
export function mayRender(verdict: CapabilityVerdict): boolean {
  return verdict.state !== 'denied';
}

export type CapabilitiesRead =
  | { readonly state: 'loading' }
  | { readonly state: 'answered'; readonly data: MeCapabilities }
  /** The platform predates the read (404) — a designed absence. */
  | { readonly state: 'notPublished' }
  | { readonly state: 'unavailable'; readonly error: unknown };

function isRouteAbsent(error: unknown): boolean {
  if (error instanceof ApiMalformedProblemError) return error.status === 404;
  return error instanceof ApiProblemError && error.status === 404;
}

/** The session's capabilities read, shared by every control. */
export function useMyCapabilities(): CapabilitiesRead {
  const { i18n } = useTranslation();
  const { subject } = useAccessGrants();
  const query = useQuery<MeCapabilities, Error>({
    // Per signed-in subject: a different account is a different answer.
    queryKey: [myCapabilities.operationId, subject],
    queryFn: ({ signal }) => callOperation(myCapabilities, {}, { language: i18n.language, signal }),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    retry: (count, error) => !(error instanceof ApiProblemError && error.status < 500) && count < 1,
  });
  if (query.data !== undefined) {
    return Array.isArray(query.data.capabilities)
      ? { state: 'answered', data: query.data }
      : { state: 'unavailable', error: new Error('capabilities: the answer carries no catalogue') };
  }
  if (query.isError) return isRouteAbsent(query.error) ? { state: 'notPublished' } : { state: 'unavailable', error: query.error };
  return { state: 'loading' };
}

/** One (kind, act)'s verdict for this person, from the session's read. */
export function useCapability(kind: CapabilityKind, action: CapabilityAction, where: CapabilityWhere = {}): CapabilityVerdict {
  const read = useMyCapabilities();
  return judgeCapability(read.state === 'answered' ? read.data : undefined, kind, action, where);
}

/** Each denial's words, spelled out for the catalogue check. */
export const WHY_NOT_KEY: Readonly<Record<DeniedReason, string>> = {
  not_granted: 'capability.whyNot.notGranted',
  unscoped: 'capability.whyNot.unscoped',
  not_wired: 'capability.whyNot.notWired',
  no_route: 'capability.whyNot.noRoute',
  out_of_scope: 'capability.whyNot.outOfScope',
  not_declared: 'capability.whyNot.notDeclared',
  other: 'capability.whyNot.other',
};
